import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { notification } from "antd";
import axios from "axios";
import { useWatiSocket } from "./WatiSocketContext";
import { useAuth } from "./AuthContext";
import { fetchChannelAssignments } from "../qv4/helpers";
import { useWatiChatList } from "../qv4/useWatiChatList";
import watiIncomingSound from "../../assets/sounds/wati_incoming_sound.mp3";
import whatsappIcon from "../../assets/images/whatsapp-96.png";

const SEND_ENDPOINT = "https://srm.uzdpart.com:33123/whatsapp/send_wati_general_message";
const QV4_CHAT_API = "https://srm.uzdpart.com:33123/wati_messages/fetch_messages_v2";

// Rendered inside Ant Design's notification portal — must not use any React context hooks.
// All data is passed as plain props captured at notification creation time.
const QuickReplyBox = ({ phone, userName, notificationKey, onNavigate, showCloseAll, onCloseAll, channelId }) => {
    const [text, setText] = React.useState("");
    const [sending, setSending] = React.useState(false);
    const [sent, setSent] = React.useState(false);

    const handleSend = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            await axios.post(SEND_ENDPOINT, {
                phone,
                message: text.trim(),
                user: userName,
                channelId,
            });
            setSent(true);
            setTimeout(() => notification.destroy(notificationKey), 800);
        } catch {
            setSending(false);
        }
    };

    return (
        <div onClick={e => e.stopPropagation()} style={{ marginTop: 6 }}>
            {sent ? (
                <div style={{ textAlign: "center", color: "#52c41a", fontWeight: 600, padding: "2px 0", fontSize: 12 }}>
                    ✓ Sent!
                </div>
            ) : (
                <>
                    <textarea
                        value={text}
                        onChange={e => setText(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        placeholder="Quick reply… (Enter to send)"
                        rows={1}
                        style={{
                            width: "100%",
                            resize: "none",
                            borderRadius: 5,
                            border: "1px solid #d9d9d9",
                            padding: "3px 7px",
                            fontSize: 11,
                            outline: "none",
                            boxSizing: "border-box",
                        }}
                    />
                    <div style={{ display: "flex", gap: 5, marginTop: 4 }}>
                        <button
                            onClick={handleSend}
                            disabled={!text.trim() || sending}
                            style={{
                                flex: 1,
                                backgroundColor: !text.trim() || sending ? "#d1d1d1" : "#075e54",
                                color: "white",
                                border: "none",
                                borderRadius: 5,
                                padding: "4px 6px",
                                cursor: !text.trim() || sending ? "not-allowed" : "pointer",
                                fontSize: 11,
                                fontWeight: 600,
                            }}
                        >
                            {sending ? "Sending…" : "Send ↵"}
                        </button>
                        <button
                            onClick={onNavigate}
                            style={{
                                backgroundColor: "transparent",
                                color: "#1677ff",
                                border: "1px solid #1677ff",
                                borderRadius: 5,
                                padding: "4px 8px",
                                cursor: "pointer",
                                fontSize: 11,
                                whiteSpace: "nowrap",
                            }}
                        >
                            Go to chat →
                        </button>
                    </div>
                    {showCloseAll && (
                        <button
                            onClick={onCloseAll}
                            style={{
                                width: "100%",
                                marginTop: 3,
                                backgroundColor: "transparent",
                                color: "#aaa",
                                border: "none",
                                padding: "2px 0",
                                cursor: "pointer",
                                fontSize: 10,
                            }}
                        >
                            Dismiss all
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

const WatiChatContext = createContext();

export const useWatiChat = () => {
    const context = useContext(WatiChatContext);
    if (!context) {
        throw new Error("useWatiChat must be used within a WatiChatProvider");
    }
    return context;
};

export const WatiChatProvider = ({ children }) => {
    const { watiChatUpdates, isWatiConnected } = useWatiSocket();
    const { authState } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Supplier-channel preload — feeds the "WhatsApp Messages" nav badge and
    // seeds WhatsappMessages' own chat list. Channel resolved from uzd_misc
    // mode 'wati_channel_assignments' rather than hardcoded, since WATI
    // supports up to 4 channels and which one is "the supplier channel" is a
    // config decision, not a frontend constant.
    const [supplierChannelId, setSupplierChannelId] = useState(null);
    const [chatlist, setChatlist] = useState([]);
    const [chatlistLoading, setChatlistLoading] = useState(true);
    const [continuationToken, setContinuationToken] = useState(null);
    const [chatlistCount, setChatlistCount] = useState(null);
    const [qv4Chatlist, setQv4Chatlist] = useState([]);
    const [qv4ChatsLoading, setQv4ChatsLoading] = useState(false);
    // The preload's own pagination cursor — previously discarded, which meant
    // whichever qv4 page seeded its chat list from this cache had no way to
    // page beyond it: "Load More" always re-requested page 1.
    const [qv4ChatlistToken, setQv4ChatlistToken] = useState(null);
    // Same story for the total-count badge — also discarded, so it never
    // showed anything when the page loaded from this cache (the usual case).
    const [qv4ChatlistCount, setQv4ChatlistCount] = useState(null);

    // Also mirrored into a ref so the socket handler below (which only
    // re-runs when watiChatUpdates changes) always reads the latest value
    // instead of whatever it closed over at its last run.
    const supplierChannelIdRef = useRef(null);
    useEffect(() => {
        fetchChannelAssignments().then(a => {
            setSupplierChannelId(a.supplier_support || null);
            supplierChannelIdRef.current = a.supplier_support || null;
        });
    }, []);

    // ── Shared chat panel state (Phase 2) ───────────────────────────────────
    // Lives here, not in a per-page hook, so QuerysetV4 and TaskManagement see
    // the exact same chat list/filter/search/pagination regardless of which
    // is currently mounted — switching pages used to silently reset the
    // filter to "All Chats" and drop any pages loaded past the initial ~100.
    const {
        activeChats, setActiveChats,
        chatsLoading, chatsLoadingMore, hasMoreChats, setHasMoreChats,
        chatSearch, setChatSearch, chatSearchFilter, setChatSearchFilter,
        chatFilters, chatFiltersLoading, selectedFilterIndex, setSelectedFilterIndex, chatTotalCount,
        setChatContinuationToken, chatContinuationTokenRef,
        buildChatV2Payload, currentFilterParams, fetchActiveChats, loadMoreChats, onSelectFilter,
    } = useWatiChatList({
        authState,
        seed: { list: qv4Chatlist, loading: qv4ChatsLoading, token: qv4ChatlistToken, count: qv4ChatlistCount },
    });

    const watiProcessedCountRef = useRef(0);
    // WhatsappMessages writes the currently open chat ID here so the
    // socket handler can keep unreadCount at 0 for that chat.
    const activeChatIdRef = useRef(null);
    const notificationAudioRef = useRef(new Audio(watiIncomingSound));
    const activeNotifsRef = useRef(new Set());
    const disconnectTimerRef = useRef(null);
    const wasDownRef = useRef(false);

    const getAllChats = async () => {
        if (!supplierChannelId) return;
        setChatlistLoading(true);
        try {
            const res = await axios.post(
                QV4_CHAT_API,
                { userId: authState?.id, continuationToken: "", filterType: 0, subConditions: null, searchString: null, searchOptionType: null, channelIds: [supplierChannelId] },
                { headers: { accessToken: localStorage.getItem("accessToken") } }
            );
            if (res.data?.success) {
                setChatlist(res.data.data?.final_data || []);
                setContinuationToken(res.data.data?.continuationToken || null);
                setChatlistCount(res.data.data?.count ?? null);
            }
        } catch (_) {
            // silent — WhatsappMessages shows error toasts for user-triggered reloads
        } finally {
            setChatlistLoading(false);
        }
    };

    // Load chatlist globally once the user is authenticated and the supplier
    // channel has been resolved from uzd_misc.
    useEffect(() => {
        if (!authState?.isAuthenticated || !supplierChannelId) return;
        getAllChats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState?.isAuthenticated, supplierChannelId]);

    // Load QV4 chatlist (channelIds: "") — used for unread badge AND to seed QuerysetV4 on open
    useEffect(() => {
        const load = async () => {
            setQv4ChatsLoading(true);
            try {
                const res = await axios.post(
                    QV4_CHAT_API,
                    { userId: authState?.id, continuationToken: "", filterType: 0, subConditions: null, searchString: null, searchOptionType: null },
                    { headers: { accessToken: localStorage.getItem("accessToken") } }
                );
                if (res.data?.success) {
                    setQv4Chatlist(res.data.data?.final_data || []);
                    setQv4ChatlistToken(res.data.data?.continuationToken || null);
                    setQv4ChatlistCount(res.data.data?.count ?? null);
                }
            } catch (_) {}
            finally { setQv4ChatsLoading(false); }
        };
        if (authState?.id) load();
    }, [authState?.id]);

    // Reload chatlists when socket reconnects after being down for >10 seconds
    useEffect(() => {
        if (!isWatiConnected) {
            // Start a timer — if still disconnected after 10s, flag it
            disconnectTimerRef.current = setTimeout(() => {
                wasDownRef.current = true;
            }, 10000);
        } else {
            clearTimeout(disconnectTimerRef.current);
            if (wasDownRef.current) {
                wasDownRef.current = false;
                // Reload both chatlists silently then notify
                Promise.all([
                    getAllChats(),
                    authState?.id ? axios.post(
                        QV4_CHAT_API,
                        { userId: authState?.id, continuationToken: "", filterType: 0, subConditions: null, searchString: null, searchOptionType: null },
                        { headers: { accessToken: localStorage.getItem("accessToken") } }
                    ).then(res => {
                        if (res.data?.success) {
                            setQv4Chatlist(res.data.data?.final_data || []);
                            setQv4ChatlistToken(res.data.data?.continuationToken || null);
                            setQv4ChatlistCount(res.data.data?.count ?? null);
                        }
                    }).catch(() => {}) : Promise.resolve(),
                ]).then(() => {
                    notification.info({
                        message: "Chat list refreshed",
                        description: "WhatsApp reconnected after a brief outage — chat data has been reloaded to catch up on missed updates.",
                        placement: "bottomLeft",
                        duration: 6,
                        icon: <img src={whatsappIcon} style={{ width: 20, height: 20 }} alt="WhatsApp" />,
                    });
                });
            }
        }
        return () => clearTimeout(disconnectTimerRef.current);
    }, [isWatiConnected]);

    // Process incoming socket events — chatlist upsert only
    useEffect(() => {
        if (watiChatUpdates.length <= watiProcessedCountRef.current) return;

        const newUpdates = watiChatUpdates.slice(watiProcessedCountRef.current);
        watiProcessedCountRef.current = watiChatUpdates.length;

        newUpdates.forEach(update => {
            // WATI hub protocol sends some events as arrays: [{type, target, arguments}]
            if (update.target === "catchSelectedConversation") {
                const chatId = update["0"];
                if (chatId) {
                    setChatlist(prev => prev.map(c => c.id === chatId ? { ...c, unreadCount: 0 } : c));
                    setQv4Chatlist(prev => prev.map(c => c.id === chatId ? { ...c, unreadCount: 0 } : c));
                }
                return;
            }

            if (
                update.target === "sendTextMessage" &&
                supplierChannelIdRef.current &&
                update["0"]?.message?.conViewModel?.channelId === supplierChannelIdRef.current
            ) {
                const conv = update["0"].message.conViewModel;
                const isOpen = activeChatIdRef.current === update["0"].conversationId;

                const updatedEntry = {
                    id: conv.id,
                    name: conv.name,
                    lastUpdated: conv.lastUpdated,
                    lastMessageDate: conv.lastMessageDate,
                    lastIncomingDate: conv.lastIncomingDate,
                    type: conv.type,
                    text: conv.text,
                    lastMessageType: update["0"].messageType,
                    unreadCount: isOpen ? 0 : conv.unreadCount,
                    isStar: conv.isStar,
                    isBlocked: conv.isBlocked,
                    whatsappId: conv.whatsappId,
                    contactId: conv.contactId,
                    activeTicketType: conv.activeTicketType,
                    ticket: conv.ticket,
                    replyDeadline: conv.replyDeadline,
                    channelName: conv.channelName,
                };

                setChatlist(prev => {
                    const exists = prev.some(c => c.id === updatedEntry.id);
                    const next = exists
                        ? prev.map(c => c.id === updatedEntry.id ? updatedEntry : c)
                        : [...prev, updatedEntry];
                    return [...next].sort((a, b) => parseFloat(b.lastUpdated) - parseFloat(a.lastUpdated));
                });

                if (update["0"].message.isOwner === false) {
                    const soundEnabled = localStorage.getItem("wati_qv4_sound_enabled") !== "false";
                    if (soundEnabled) {
                        notificationAudioRef.current.currentTime = 0;
                        notificationAudioRef.current.play().catch(() => { });
                    }

                    const notifsEnabled = localStorage.getItem("wati_notifications_enabled") !== "false";
                    if (notifsEnabled && location.pathname !== "/whatsapp_messages") {
                        const nKey = `wati-notif-${Date.now()}`;
                        const userName = authState?.user?.name;
                        const convId = conv.id;
                        const msgId = update["0"].messageId;
                        const showCloseAll = activeNotifsRef.current.size >= 1;
                        const onCloseAll = () => {
                            activeNotifsRef.current.forEach(k => notification.destroy(k));
                            activeNotifsRef.current.clear();
                        };
                        activeNotifsRef.current.add(nKey);
                        const placement = localStorage.getItem("wati_notification_position") || "bottomLeft";
                        notification.info({
                            key: nKey,
                            message: <span style={{ fontSize: 13, fontWeight: 600 }}>{conv.name}</span>,
                            description: (
                                <>
                                    <div style={{ fontSize: 12, color: "#555", marginBottom: 4, lineHeight: 1.4 }}>
                                        💬 {conv.text || "(media)"}
                                    </div>
                                    <QuickReplyBox
                                        phone={conv.whatsappId}
                                        userName={userName}
                                        notificationKey={nKey}
                                        channelId={supplierChannelIdRef.current}
                                        showCloseAll={showCloseAll}
                                        onCloseAll={onCloseAll}
                                        onNavigate={() => {
                                            notification.destroy(nKey);
                                            navigate("/whatsapp_messages", { state: { openChatId: convId, openMessageId: msgId } });
                                        }}
                                    />
                                </>
                            ),
                            placement,
                            duration: 8,
                            icon: <img src={whatsappIcon} style={{ width: 20, height: 20 }} alt="WhatsApp" />,
                            style: { width: 300 },
                            onClose: () => activeNotifsRef.current.delete(nKey),
                        });
                    }
                }
            }

            // QV4 channel (channelId === null) — track unread count + play sound
            if (update.target === "sendTextMessage" && update["0"]?.message?.conViewModel?.channelId === null) {
                const conv = update["0"].message.conViewModel;
                const fullEntry = {
                    id: conv.id,
                    name: conv.name,
                    text: conv.text,
                    messageType: update["0"].messageType,
                    lastUpdated: conv.lastUpdated,
                    lastIncomingDate: conv.lastIncomingDate,
                    type: conv.type,
                    unreadCount: conv.unreadCount || 0,
                    isStar: conv.isStar,
                    whatsappId: conv.whatsappId,
                    activeTicketType: conv.activeTicketType,
                    ticket: conv.ticket,
                    replyDeadline: conv.replyDeadline,
                };
                setQv4Chatlist(prev => {
                    const exists = prev.some(c => c.id === conv.id);
                    if (!exists) return [...prev, fullEntry];
                    return prev.map(c => c.id === conv.id ? { ...c, ...fullEntry } : c);
                });
                if (update["0"].message.isOwner === false) {
                    const soundEnabled = localStorage.getItem("wati_qv4_sound_enabled") !== "false";
                    if (soundEnabled) {
                        notificationAudioRef.current.currentTime = 0;
                        notificationAudioRef.current.play().catch(() => {});
                    }
                }
            }
        });
    }, [watiChatUpdates]);

    const totalUnread = chatlist.filter(c => c.unreadCount > 0).length;
    const qv4TotalUnread = qv4Chatlist.filter(c => (c.unreadCount || 0) > 0).length;

    return (
        <WatiChatContext.Provider value={{
            chatlist,
            setChatlist,
            chatlistLoading,
            setChatlistLoading,
            continuationToken,
            setContinuationToken,
            chatlistCount,
            getAllChats,
            supplierChannelId,
            activeChatIdRef,
            totalUnread,
            qv4TotalUnread,
            qv4Chatlist,
            qv4ChatsLoading,
            qv4ChatlistToken,
            qv4ChatlistCount,
            activeChats, setActiveChats,
            chatsLoading, chatsLoadingMore, hasMoreChats, setHasMoreChats,
            chatSearch, setChatSearch, chatSearchFilter, setChatSearchFilter,
            chatFilters, chatFiltersLoading, selectedFilterIndex, setSelectedFilterIndex, chatTotalCount,
            setChatContinuationToken, chatContinuationTokenRef,
            buildChatV2Payload, currentFilterParams, fetchActiveChats, loadMoreChats, onSelectFilter,
        }}>
            {children}
        </WatiChatContext.Provider>
    );
};
